import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UseCaseChat } from '../../entities/useCaseChat/UseCaseChat';
import type { UseCase } from '../../entities/useCaseChat/UseCase';
import { DashboardStore } from '../../stores/DashboardStore';
import { AllUseCaseChatsCache } from '../../stores/AllUseCaseChatsCache';
import { getUseCaseService } from '../../services/UseCaseService';
import { getRiskReportService } from '../../services/RiskPlusReportService';
import { FileText } from 'lucide-react';
import { ImpactPlusReportStore } from '../../stores/ImpactPlusReportStore';
import { ImpactReportDetailModal } from '../ImpactPlusReport/ImpactReportModal';
import { RiskPlusReportStore } from '../../stores/RiskPlusReportStore';
import { ReportDetailModal as RiskReportDetailModal } from '../RiskPlusReport/RiskReportModal';
import { riskColor, riskLabel } from '../risk-plus/RiskPlusPageShell';
import UseCaseSummary from '../Chat/ChatTabs/UseCaseSummary';
import IntendedUseForm from '../Chat/ChatTabs/IntendedUse';
import BusinessContextForm from '../Chat/ChatTabs/BusinessContext';
import UseCaseDataContextForm from '../Chat/ChatTabs/DataContext';
import SystemForm from '../Chat/ChatTabs/SystemFile';
import { GenerateReportButton } from '../../components/GenerateReportButton';
import type { RiskPlusReport } from '../../entities/report/RiskPlusReport';
import type { ImpactPlusReport } from '../../entities/report/schemas/ImpactPlusReport.schema';
import { useReportModalParam } from '../../hooks/useReportModalParam';
import './use-case-overview.css';

function isFilled(value: string): boolean {
  return value != null && value.trim() !== '';
}

function countRecord(values: Record<string, string>): { done: number; total: number } {
  const entries = Object.values(values);
  return { total: entries.length, done: entries.filter(isFilled).length };
}

type CtxTab = { label: string; done: number; total: number };

function ctxTabsFor(useCase: UseCase): CtxTab[] {
  const { useCaseSummary, intendedUse, businessContext, dataContext, systemComponents } = useCase;

  const dataTotals = (['training', 'input', 'output'] as const)
    .map((k) => countRecord({ ...dataContext[k] }))
    .reduce((acc, c) => ({ done: acc.done + c.done, total: acc.total + c.total }), { done: 0, total: 0 });

  return [
    { label: 'Summary', ...countRecord({ ...useCaseSummary }) },
    { label: 'Intended Use', ...countRecord({ ...intendedUse }) },
    { label: 'Context', ...countRecord({ ...businessContext }) },
    { label: 'Data', ...dataTotals },
    { label: 'System', ...countRecord({ ...systemComponents }) },
  ];
}

type ContextStatus = 'not_started' | 'in_progress' | 'captured';

function deriveContextStatus(tabs: CtxTab[]): ContextStatus {
  const total = tabs.reduce((sum, t) => sum + t.total, 0);
  const done = tabs.reduce((sum, t) => sum + t.done, 0);
  if (done === 0) return 'not_started';
  if (done >= total) return 'captured';
  return 'in_progress';
}

const CONTEXT_STATUS_META: Record<ContextStatus, { cls: string; label: string }> = {
  not_started: { cls: 'st-notstarted', label: 'Not Started' },
  in_progress: { cls: 'st-progress', label: 'In Progress' },
  captured: { cls: 'st-captured', label: 'Captured' },
};

function displayUseCaseTitle(title: string): string {
  if (!title || title === 'N/A' || title === '<untitled>') return 'Untitled use case';
  return title;
}

const CTX_TABS_ORDER = ['Summary', 'Intended Use', 'Context', 'Data', 'System'] as const;

const ImpactPlusUseCaseOverview = () => {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string; section?: string }>();
  const [searchParams] = useSearchParams();
  const impactPlusReportId = searchParams.get('impactPlusReportId') ?? '';
  const navigate = useNavigate();

  const isLoadingBriefs = DashboardStore.useDashboardStore((s) => s.isLoadingUseCaseBriefs);
  const loadUseCaseBriefs = DashboardStore.useDashboardStore((s) => s.loadUseCaseBriefs);
  const useCaseBriefs = DashboardStore.useDashboardStore((s) => s.useCaseBriefs);

  const useCaseChats = AllUseCaseChatsCache.useAllUseCaseChatsCache((s) => s.useCaseChats);

  const brief = useMemo(
    () => useCaseBriefs.find((b) => b.useCaseChatId === useCaseChatId),
    [useCaseBriefs, useCaseChatId],
  );

  const [resolvedChat, setResolvedChat] = useState<UseCaseChat | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fetchingChat, setFetchingChat] = useState(false);
  const [activeTab, setActiveTab] = useState<(typeof CTX_TABS_ORDER)[number]>('Summary');

  const [impactPlusReport, setImpactPlusReport] = useState<ImpactPlusReport | null>(null);
  const [isLoadingImpactReport, setIsLoadingImpactReport] = useState(false);
  const [impactReportError, setImpactReportError] = useState<string | null>(null);

  const [riskReport, setRiskReport] = useState<RiskPlusReport | null>(null);
  const [isLoadingRiskReport, setIsLoadingRiskReport] = useState(false);
  const [riskReportError, setRiskReportError] = useState<string | null>(null);

  type UseCaseChatReportItem = { reportType: string; reportTitle: string; reportId: string };
  const [useCaseChatReports, setUseCaseChatReports] = useState<{
    impactPlusReport: UseCaseChatReportItem | null;
    riskPlusReport: UseCaseChatReportItem | null;
  } | null>(null);

  const impactReportModal = useReportModalParam('impactPlusReportModal');
  const loadImpactPlusReportIntoStore = ImpactPlusReportStore.useImpactPlusReportStore((s) => s.loadImpactPlusReport);
  const setImpactPlusReportInStore = ImpactPlusReportStore.useImpactPlusReportStore((s) => s.setImpactPlusReport);

  const riskReportModal = useReportModalParam('riskPlusReportModal');
  const loadRiskPlusReportIntoStore = RiskPlusReportStore.useRiskPlusReportStore((s) => s.loadReportData);
  const setRiskPlusReport = RiskPlusReportStore.useRiskPlusReportStore((s) => s.setRiskPlusReport);

  useEffect(() => {
    if (!impactReportModal.reportId) return;
    void loadImpactPlusReportIntoStore(impactReportModal.reportId);
  }, [impactReportModal.reportId, loadImpactPlusReportIntoStore]);

  useEffect(() => {
    if (!riskReportModal.reportId) return;
    void loadRiskPlusReportIntoStore(riskReportModal.reportId);
  }, [riskReportModal.reportId, loadRiskPlusReportIntoStore]);

  useEffect(() => {
    void loadUseCaseBriefs();
  }, [loadUseCaseBriefs]);

  const loadChat = useCallback(async () => {
    if (!useCaseChatId) return;
    setLoadError(null);

    const cached = useCaseChats.find((c) => c.id === useCaseChatId);
    if (cached?.id && cached.useCase) setResolvedChat(cached);
    setFetchingChat(true);
    try {
      const chat = await getUseCaseService().getUseCaseById(useCaseChatId);
      setResolvedChat(chat);
    } catch {
      setLoadError('Could not load this use case.');
    } finally {
      setFetchingChat(false);
    }
  }, [useCaseChatId, useCaseChats]);

  useEffect(() => {
    void loadChat();
  }, [loadChat]);

  const loadImpactPlusReport = useCallback(async () => {
    if (!impactPlusReportId) {
      setImpactPlusReport(null);
      setImpactReportError(null);
      setIsLoadingImpactReport(false);
      return;
    }
    setImpactReportError(null);
    setIsLoadingImpactReport(true);
    try {
      const report = await getUseCaseService().getImpactPlusReport(impactPlusReportId);
      setImpactPlusReport(report);
    } catch (err) {
      setImpactPlusReport(null);
      setImpactReportError(err instanceof Error ? err.message : 'Could not load Impact+ report.');
    } finally {
      setIsLoadingImpactReport(false);
    }
  }, [impactPlusReportId]);

  useEffect(() => {
    void loadImpactPlusReport();
  }, [loadImpactPlusReport]);

  useEffect(() => {
    if (!useCaseChatId) return;
    void getUseCaseService()
      .getUseCaseChatReports(useCaseChatId)
      .then(setUseCaseChatReports)
      .catch(() => setUseCaseChatReports(null));
  }, [useCaseChatId]);

  const riskPlusReportId = useCaseChatReports?.riskPlusReport?.reportId ?? null;

  const loadRiskReport = useCallback(async () => {
    if (!riskPlusReportId) {
      setRiskReport(null);
      setRiskReportError(null);
      setIsLoadingRiskReport(false);
      return;
    }
    setRiskReportError(null);
    setIsLoadingRiskReport(true);
    try {
      const report = await getRiskReportService().getRiskReportById(riskPlusReportId);
      setRiskReport(report);
    } catch (err) {
      setRiskReport(null);
      setRiskReportError(err instanceof Error ? err.message : 'Could not load Risk+ report.');
    } finally {
      setIsLoadingRiskReport(false);
    }
  }, [riskPlusReportId]);

  useEffect(() => {
    void loadRiskReport();
  }, [loadRiskReport]);

  const hasImpactReport = !!useCaseChatReports?.impactPlusReport;
  const hasRiskReport = !!useCaseChatReports?.riskPlusReport;
  const isProcessed = brief?.contentStatus === 'processed' || brief?.contentStatus === 'expertValidated';
  const isSufficient = brief?.contentStatus === 'sufficient';
  const isModified = brief?.contentStatus === 'modified';
  const showReportsPanel = isProcessed || isSufficient || isModified || hasImpactReport || hasRiskReport;

  const refreshReports = useCallback(() => {
    void getUseCaseService()
      .getUseCaseChatReports(useCaseChatId)
      .then(setUseCaseChatReports)
      .catch(() => setUseCaseChatReports(null));
    void loadUseCaseBriefs();
  }, [useCaseChatId, loadUseCaseBriefs]);

  const title = useMemo(() => {
    if (!brief) return '';
    const briefTitle = displayUseCaseTitle(brief.useCaseTitle);
    const reportTitle = impactPlusReport?.titlePage.useCaseTitle ?? '';
    if (briefTitle === 'Untitled use case' && reportTitle) {
      return reportTitle;
    }
    return briefTitle;
  }, [brief, impactPlusReport]);

  const ctxTabs = useMemo(
    () => (resolvedChat ? ctxTabsFor(resolvedChat.useCase) : null),
    [resolvedChat],
  );
  const contextStatus = useMemo(() => (ctxTabs ? deriveContextStatus(ctxTabs) : 'not_started'), [ctxTabs]);
  const contextStatusMeta = CONTEXT_STATUS_META[contextStatus];

  const backHref = '/dashboard';

  if (!useCaseChatId) return null;

  if (isLoadingBriefs) {
    return (
      <div className="ucov-page min-h-screen p-6">
        <div className="mx-auto max-w-7xl animate-pulse rounded-xl bg-white p-8 shadow-sm">
          <div className="h-4 w-1/3 rounded bg-gray-200" />
          <div className="mt-4 h-8 w-2/3 rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="ucov-page min-h-screen p-6">
        <div className="mx-auto max-w-7xl rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-gray-700">Use case not found.</p>
          <button
            type="button"
            onClick={() => navigate(backHref)}
            className="mt-4 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
          >
            Back to register
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ucov-page min-h-screen pb-10 pt-6">
      <div className="ucov mx-auto px-4 md:px-6">
        <div className="ucov-topnav">
          <Link to={backHref} className="ucov-topnav-link">
            <span style={{ lineHeight: '0.5' }}>←</span>
            Use Case Register
          </Link>
          <button
            type="button"
            className="ucov-topnav-link"
            onClick={() => {
              navigate(`/chat/${encodeURIComponent(useCaseChatId)}/edit`, {
                state: { from: `/use-case/${encodeURIComponent(useCaseChatId)}` },
              });
            }}
          >
            Resume chat →
          </button>
        </div>

        <div className="ucov-masthead">
          <div className="ucov-masthead-main">
            <div className="ucov-masthead-title">{title}</div>
            <div className="ucov-masthead-tags">
              {(brief.contentStatus === 'processed' || brief.contentStatus === 'expertValidated') && (
                <span className="ucov-status-badge processed">Processed</span>
              )}
              {brief.contentStatus === 'sufficient' && (
                <span className="ucov-status-badge processing">Ready for processing</span>
              )}
              {(brief.contentStatus === 'none' || brief.contentStatus === 'partial') && (
                <span className="ucov-status-badge insufficient">Insufficient Content</span>
              )}
              {brief.contentStatus === 'modified' && (
                <span className="ucov-status-badge modified">Modified</span>
              )}
            </div>
          </div>

          {showReportsPanel && (
            <div className="ucov-masthead-reports">
              <div className="ucov-report-label">Reports</div>
              {hasImpactReport ? (
                <div className="ucov-report-row">
                  <button
                    type="button"
                    onClick={() => impactReportModal.open(useCaseChatReports!.impactPlusReport!.reportId)}
                    className="ucov-report-link"
                  >
                    <FileText className="h-4 w-4" strokeWidth={2} />
                    Impact+ Report
                  </button>
                  {isModified && resolvedChat && (
                    <GenerateReportButton
                      useCaseChat={resolvedChat}
                      reportType="impact"
                      label="Refresh Impact+ Report"
                      variant="icon"
                      disabled={brief.jobIsActive}
                      onGenerateReport={refreshReports}
                    />
                  )}
                </div>
              ) : resolvedChat ? (
                <GenerateReportButton
                  useCaseChat={resolvedChat}
                  reportType="impact"
                  label="Generate Impact+ Report"
                  disabled={brief.jobIsActive}
                  onGenerateReport={refreshReports}
                />
              ) : null}

              {hasRiskReport ? (
                <div className="ucov-report-row">
                  <button
                    type="button"
                    onClick={() => riskReportModal.open(useCaseChatReports!.riskPlusReport!.reportId)}
                    className="ucov-report-link"
                  >
                    <FileText className="h-4 w-4" strokeWidth={2} />
                    Risk+ Report
                  </button>
                  {isModified && resolvedChat && (
                    <GenerateReportButton
                      useCaseChat={resolvedChat}
                      reportType="risk"
                      label="Refresh Risk+ Report"
                      variant="icon"
                      disabled={brief.jobIsActive}
                      onGenerateReport={refreshReports}
                    />
                  )}
                </div>
              ) : resolvedChat ? (
                <GenerateReportButton
                  useCaseChat={resolvedChat}
                  reportType="risk"
                  label="Generate Risk+ Report"
                  disabled={brief.jobIsActive}
                  onGenerateReport={refreshReports}
                />
              ) : null}

              {brief.contentStatus === 'modified' && (hasImpactReport || hasRiskReport) && (
                <span className="ucov-report-cap">
                  Context was updated in the intake after these reports ran. Refresh to bring them up to date.
                </span>
              )}
            </div>
          )}
        </div>

        <div className="ucov-outcome-strip">
          <div className="ucov-outcome-half">
            <div className="ucov-outcome-eyebrow">Impact+</div>
            {isLoadingImpactReport && !impactPlusReport ? (
              <div className="ucov-outcome-line muted">Loading…</div>
            ) : impactReportError ? (
              <div className="ucov-outcome-line muted">{impactReportError}</div>
            ) : impactPlusReport ? (
              <>
                <span className="ucov-sev-chip">
                  <span
                    className="ucov-sev-dot"
                    style={{ background: riskColor(impactPlusReport.overallRiskRank) }}
                  />
                  Stakeholder impact: {riskLabel(impactPlusReport.overallRiskRank)}
                </span>
                <div className="ucov-outcome-line">{impactPlusReport.decisionGuidance.cohortStatsSummary}</div>
                {hasImpactReport && (
                  <button
                    type="button"
                    className="ucov-btn-secondary"
                    onClick={() => {
                      const query = impactPlusReportId
                        ? `?impactPlusReportId=${encodeURIComponent(impactPlusReportId)}`
                        : '';
                      navigate(`/use-case/${encodeURIComponent(useCaseChatId)}/hazards${query}`);
                    }}
                  >
                    View Impact+ →
                  </button>
                )}
              </>
            ) : (
              <>
                <span className="ucov-await-chip">Not yet generated</span>
                <div className="ucov-outcome-line muted">Impact+ has not been run for this use case.</div>
              </>
            )}
          </div>
          <div className="ucov-outcome-divider" />
          <div className="ucov-outcome-half">
            <div className="ucov-outcome-eyebrow">Risk+</div>
            {isLoadingRiskReport && !riskReport ? (
              <div className="ucov-outcome-line muted">Loading…</div>
            ) : riskReportError ? (
              <div className="ucov-outcome-line muted">{riskReportError}</div>
            ) : riskReport ? (
              <>
                <span className="ucov-sev-chip">
                  <span
                    className="ucov-sev-dot"
                    style={{ background: riskColor(riskReport.executiveReadinessVerdict.overallRiskRank) }}
                  />
                  Inherent risk: {riskLabel(riskReport.executiveReadinessVerdict.overallRiskRank)}
                </span>
                <div className="ucov-outcome-line">{riskReport.leadershipSummary.riskExposureSummary}</div>
                {hasRiskReport && (
                  <button
                    type="button"
                    className="ucov-btn-secondary"
                    onClick={() => {
                      navigate(`/use-case/${encodeURIComponent(useCaseChatId)}/executive`);
                    }}
                  >
                    View Risk+ →
                  </button>
                )}
              </>
            ) : (
              <>
                <span className="ucov-await-chip">Not yet generated</span>
                <div className="ucov-outcome-line muted">Risk+ has not been run for this use case.</div>
              </>
            )}
          </div>
        </div>

        <div className="ucov-card">
          <div className="ucov-card-hdr">
            <span className="ucov-card-title">
              <span className="ucov-card-title-icon aq-icon" aria-hidden>
                info
              </span>
              Use Case Context
            </span>
            <span className={`ucov-status-chip ${contextStatusMeta.cls}`}>
              <span className="ucov-status-dot" />
              {contextStatusMeta.label}
            </span>
          </div>
          <div className="ucov-tabs">
            {CTX_TABS_ORDER.map((label) => {
              const tab = ctxTabs?.find((t) => t.label === label);
              return (
                <button
                  key={label}
                  type="button"
                  className={`ucov-tab${activeTab === label ? ' active' : ''}`}
                  onClick={() => setActiveTab(label)}
                >
                  {label}
                  {tab && <span className="ucov-tab-count">{tab.done}/{tab.total}</span>}
                </button>
              );
            })}
          </div>
          <div className="ucov-card-pad">
            {loadError ? (
              <p className="text-sm text-red-600" role="alert">
                {loadError}
              </p>
            ) : fetchingChat || !resolvedChat ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : activeTab === 'Summary' ? (
              <UseCaseSummary formValues={resolvedChat.useCase.useCaseSummary} onInputChange={() => { }} viewOnly />
            ) : activeTab === 'Intended Use' ? (
              <IntendedUseForm formValues={resolvedChat.useCase.intendedUse} onInputChange={() => { }} viewOnly />
            ) : activeTab === 'Context' ? (
              <BusinessContextForm formValues={resolvedChat.useCase.businessContext} onInputChange={() => { }} viewOnly />
            ) : activeTab === 'Data' ? (
              <UseCaseDataContextForm formValues={resolvedChat.useCase.dataContext} onInputChange={() => { }} viewOnly />
            ) : (
              <SystemForm formValues={resolvedChat.useCase.systemComponents} onInputChange={() => { }} viewOnly />
            )}
          </div>
        </div>
      </div>

      <ImpactReportDetailModal
        isOpen={impactReportModal.isOpen}
        onClose={() => {
          impactReportModal.close();
          setImpactPlusReportInStore(null);
        }}
      />

      <RiskReportDetailModal
        isOpen={riskReportModal.isOpen}
        onClose={() => {
          riskReportModal.close();
          setRiskPlusReport(null);
        }}
      />
    </div>
  );
};

export default ImpactPlusUseCaseOverview;
